import { Clickable, Col, Icon, Row, Text } from 'components';
import { Api, POLL_STATE, canManagePollChannel, theme } from 'lib';
import useTeamChannelUpdates from 'core/useTeamChannelUpdates';
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { IrreversableModal } from 'screens/Admin/components';
import PollProfileCard from './PollProfileCard';
import PollsListPanel from './PollsListPanel';
import PollViewModal from './PollViewModal';

// Admin-only heavy panels — code-split so fans never download them.
const PollForm = lazy(() => import('screens/Admin/screens/Polls/components/PollForm'));
const PollResultsModal = lazy(() =>
  import('screens/Admin/screens/Polls/components/PollResultsModal')
);

// Three cards on desktop keeps the row inside the profile's content column.
const VISIBLE_DESKTOP_COUNT = 3;
const PAGE_LIMIT = 10;

// Live first, then scheduled/upcoming, then closed, then anything else hidden.
const STATE_RANK = {
  [POLL_STATE.OPEN]: 0,
  [POLL_STATE.HIDDEN]: 1,
  [POLL_STATE.CLOSED]: 2
};

const isScheduled = (p) =>
  p.isActive !== false && p.startDate && new Date(p.startDate).getTime() > Date.now();

const sortPolls = (polls) =>
  [...polls].sort((a, b) => {
    const ra = STATE_RANK[a.state] ?? 99;
    const rb = STATE_RANK[b.state] ?? 99;
    if (ra !== rb) return ra - rb;
    // Upcoming/scheduled: soonest start first. Otherwise newest first.
    if (isScheduled(a) && isScheduled(b)) {
      return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
    }
    const ad = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bd = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return bd - ad;
  });

const SkeletonCard = styled.div`
  width: ${(p) => (p.isMobile ? 240 : 296)}px;
  min-width: ${(p) => (p.isMobile ? 240 : 296)}px;
  min-height: 160px;
  background: linear-gradient(90deg, ${theme.GREY_10} 0%, #f7f9fb 50%, ${theme.GREY_10} 100%);
  background-size: 200% 100%;
  border-radius: 12px;
  border: 1px solid ${theme.GREY_20};
  animation: shimmer 1.4s linear infinite;
  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const EmptyCard = styled.button`
  width: ${(p) => (p.isMobile ? 240 : 296)}px;
  min-width: ${(p) => (p.isMobile ? 240 : 296)}px;
  min-height: 160px;
  border: 1.5px dashed ${theme.GREY_30};
  background: transparent;
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: ${theme.GREY_60};
  transition: border-color 0.15s ease, color 0.15s ease;
  &:hover {
    border-color: ${theme.SECONDARY};
    color: ${theme.SECONDARY};
  }
`;

/**
 * PollsRow — Profile-page section listing a team channel's polls.
 *
 * Visibility:
 *  - Renders only for team profiles (the caller in Fan.js already gates on this).
 *  - Fans/anon: only currently-visible polls via /polls/list (open + closed; hidden
 *    are filtered server-side). Read-only — voting happens in the mobile app.
 *  - Admins (P4P or the channel owner): the full admin list including inactive /
 *    scheduled, with inline create / edit / delete / toggle / results.
 *
 * PollForm + PollResultsModal are lazy-loaded; fans never download them.
 */
const PollsRow = ({ team }) => {
  const currentUser = useSelector((state) => state.user);
  const isMobile = useSelector((state) => state.appState.isMobile);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const teamId = team?._id;
  const canManage = canManagePollChannel(currentUser, teamId);

  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchingMoreRef = useRef(false);
  const endReachedRef = useRef(false);
  const pollsCountRef = useRef(0);
  const [hasMore, setHasMore] = useState(false);

  const [formMode, setFormMode] = useState(null); // null | 'create' | 'edit'
  const [editingPoll, setEditingPoll] = useState(null);
  const [resultsPoll, setResultsPoll] = useState(null); // admin results panel
  const [viewPoll, setViewPoll] = useState(null); // fan read-only view
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [listPanelOpen, setListPanelOpen] = useState(false);

  // Single fetch path that switches by role.
  const fetchPolls = useCallback(
    async ({ append = false } = {}) => {
      if (!teamId) return;
      if (!currentUser) {
        setPolls([]);
        setLoading(false);
        return;
      }
      if (append) {
        if (fetchingMoreRef.current || endReachedRef.current) return;
        fetchingMoreRef.current = true;
      } else {
        endReachedRef.current = false;
        setLoading(true);
      }

      const offset = append ? pollsCountRef.current : 0;
      let nextChunk = [];
      let totalKnown = null;

      if (canManage) {
        const params = new URLSearchParams({
          teamId,
          limit: String(PAGE_LIMIT),
          offset: String(offset)
        });
        const res = await Api.get(`/admin/polls/list?${params.toString()}`, null, { silent: true });
        const data = Array.isArray(res?.data) ? res.data : [];
        // The admin list resolves  server-side, so take it as given.
        nextChunk = data;
        totalKnown = res?.pagination?.total ?? null;
      } else {
        const res = await Api.get(
          `/polls/list?teamId=${teamId}&limit=${PAGE_LIMIT}&offset=${offset}`,
          null,
          { silent: true }
        );
        const list = Array.isArray(res?.polls) ? res.polls : [];
        // Fan list is already active + non-hidden; mark active so card live logic works.
        nextChunk = list.map((p) => ({ ...p, isActive: true }));
        totalKnown = res?.pagination?.total ?? null;
      }

      const newTotal = (append ? pollsCountRef.current : 0) + nextChunk.length;
      const reachedEnd =
        nextChunk.length < PAGE_LIMIT || (totalKnown !== null && newTotal >= totalKnown);
      endReachedRef.current = reachedEnd;
      setHasMore(!reachedEnd);

      setPolls((prev) => {
        const next = sortPolls(append ? [...prev, ...nextChunk] : nextChunk);
        pollsCountRef.current = next.length;
        return next;
      });

      if (append) fetchingMoreRef.current = false;
      else setLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [teamId, canManage, currentUser]
  );

  useEffect(() => {
    pollsCountRef.current = polls.length;
  }, [polls.length]);

  useEffect(() => {
    fetchPolls({ append: false });
  }, [fetchPolls]);

  const loadMore = useCallback(() => fetchPolls({ append: true }), [fetchPolls]);

  // Realtime — live insert / close / hide / delete on TEAM_CHANNEL_UPDATE, no reload.
  const refetchTimerRef = useRef(null);
  const scheduleRefetch = useCallback(() => {
    if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
    refetchTimerRef.current = setTimeout(() => fetchPolls({ append: false }), 350);
  }, [fetchPolls]);
  useEffect(() => () => clearTimeout(refetchTimerRef.current), []);

  useTeamChannelUpdates(
    teamId,
    useCallback(
      (evt) => {
        if (!evt || evt.entity !== 'poll') return;
        if (evt.action === 'deleted') {
          setPolls((prev) => prev.filter((p) => p._id !== evt.id));
          pollsCountRef.current = Math.max(0, pollsCountRef.current - 1);
          return;
        }
        // Every vote → merge the fresh breakdown live. But only surface results to a
        // fan already entitled to see them (they voted, or the poll is closed) — the
        // server withholds results on an open, unvoted poll and we must not leak them.
        if (evt.action === 'results_updated' && evt.data) {
          setPolls((prev) =>
            prev.map((p) => {
              if (p._id !== evt.id) return p;
              const entitled = p.answered === true || p.state === POLL_STATE.CLOSED || !!p.results;
              return {
                ...p,
                totalSubmissions: evt.data.totalSubmissions ?? p.totalSubmissions,
                ...(entitled ? { results: evt.data } : {})
              };
            })
          );
          return;
        }
        // created / updated / state_changed → re-fetch so fan-scoped results/answered
        // and hidden-filtering stay correct (→ hidden drops the card; → closed reveals).
        scheduleRefetch();
      },
      [scheduleRefetch]
    )
  );

  const handleCreate = () => {
    setEditingPoll(null);
    setFormMode('create');
  };

  const handleEdit = (poll) => {
    setEditingPoll(poll);
    setFormMode('edit');
  };

  const handleVoted = useCallback((updated) => {
    setPolls((prev) => prev.map((p) => (p._id === updated._id ? { ...p, ...updated } : p)));
  }, []);

  const handleView = (poll) => {
    // Admins inspect via the full results panel (admin endpoint); fans get the
    // read-only viewer built from the list payload (no extra request, no voting).
    if (canManage) setResultsPoll(poll);
    else setViewPoll(poll);
  };

  const handleDelete = (poll) => setDeleteTarget(poll);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const ok = await Api.delete(`/admin/polls/delete/${deleteTarget._id}`, null, null, {
      silent: true
    });
    setDeleting(false);
    if (ok === true) {
      setPolls((prev) => prev.filter((p) => p._id !== deleteTarget._id));
      pollsCountRef.current = Math.max(0, pollsCountRef.current - 1);
      dispatch({ type: 'HANDLE_RES', payload: { res: t('pollQuizProfile.pollDeleted') } });
      setDeleteTarget(null);
    } else {
      dispatch({
        type: 'HANDLE_RES',
        payload: { err: ok?.err || t('pollQuizProfile.pollDeleteError') }
      });
      setDeleteTarget(null);
    }
  };

  // Optimistic toggle — patch immediately, roll back on failure (surface conflicts).
  const handleToggleActive = useCallback(async (poll) => {
    const next = !poll.isActive;
    setPolls((prev) =>
      prev.map((p) =>
        p._id === poll._id ? { ...p, isActive: next, state: next ? p.state : POLL_STATE.HIDDEN } : p
      )
    );
    const res = await Api.put('/admin/polls/update', { id: poll._id, isActive: next }, null, {
      silent: true
    });
    if (!res || res.err) {
      setPolls((prev) =>
        prev.map((p) =>
          p._id === poll._id
            ? { ...p, isActive: !next, state: poll.state }
            : p
        )
      );
      if (res?.err) dispatch({ type: 'HANDLE_RES', payload: { err: res.err } });
    } else if (res.data) {
      setPolls((prev) =>
        prev.map((p) =>
          p._id === poll._id ? { ...p, ...res.data } : p
        )
      );
    }
  }, [dispatch]);

  const handleSaved = () => {
    fetchPolls();
    dispatch({ type: 'HANDLE_RES', payload: { res: t('pollQuizProfile.pollSaved') } });
    setFormMode(null);
    setEditingPoll(null);
  };

  const visiblePolls = useMemo(
    () => (isMobile ? polls : polls.slice(0, VISIBLE_DESKTOP_COUNT)),
    [polls, isMobile]
  );
  const hiddenCount = Math.max(0, polls.length - VISIBLE_DESKTOP_COUNT);
  const showSeeAll = !isMobile && (hiddenCount > 0 || hasMore);

  // Nothing to show and the viewer can't manage → render nothing.
  if (!teamId) return null;
  if (!currentUser && !canManage) return null;
  if (!loading && !canManage && visiblePolls.length === 0) return null;

  return (
    <>
      <Col wid="100%" marg="0">
        <Row
          pad={isMobile ? '0 16px' : '0'}
          between
          center
          wid={isMobile ? 'calc(100% - 32px)' : '100%'}
        >
          <Row noFlex center gap="14px">
            <Text h2>{t('pollQuizProfile.pollsTitle')}</Text>
            {canManage && (
              <Clickable onClick={handleCreate}>
                <Row center gap="6px">
                  <Icon type="add" dimensions={18} col={theme.SECONDARY} />
                  <Text b2 bold col={theme.SECONDARY}>
                    {t('pollQuizProfile.add')}
                  </Text>
                </Row>
              </Clickable>
            )}
          </Row>

          {showSeeAll && (
            <Clickable onClick={() => setListPanelOpen(true)}>
              <Text b2 bold col={theme.GREY_70}>
                {t('pollQuizProfile.seeAllCount', { count: polls.length })}
              </Text>
            </Clickable>
          )}
        </Row>

        <Row
          start
          wid={isMobile ? '100vw' : '100%'}
          scrollX
          marg={isMobile ? '12px 0 0' : '16px 0 0'}
          gap={isMobile ? '12px' : '24px'}
          pos="relative"
          style={{ scrollbarWidth: 'none' }}
        >
          {loading ? (
            <>
              {[0, 1, 2].map((i) => (
                <Row key={i} marg={isMobile && i === 0 ? '0 0 0 16px' : '0'}>
                  <SkeletonCard isMobile={isMobile} />
                </Row>
              ))}
            </>
          ) : (
            <>
              {canManage && visiblePolls.length === 0 && (
                <Row marg={isMobile ? '0 0 0 16px' : '0'}>
                  <EmptyCard isMobile={isMobile} onClick={handleCreate}>
                    <Icon type="add" dimensions={24} col="currentColor" />
                    <Text med col="currentColor">
                      {t('pollQuizProfile.createFirstPoll')}
                    </Text>
                    <Text c2 col={theme.GREY_50} center>
                      {t('pollQuizProfile.createFirstPollSub')}
                    </Text>
                  </EmptyCard>
                </Row>
              )}

              {visiblePolls.map((p, i) => (
                <Row marg={isMobile && i === 0 ? '0 0 0 16px' : '0'} key={p._id}>
                  <PollProfileCard
                    poll={p}
                    isMobile={isMobile}
                    canManage={canManage}
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggleActive={handleToggleActive}
                  />
                </Row>
              ))}
            </>
          )}
        </Row>
      </Col>

      <PollsListPanel
        visible={listPanelOpen}
        polls={polls}
        loading={loading}
        hasMore={hasMore}
        onLoadMore={loadMore}
        canManage={canManage}
        onClose={() => setListPanelOpen(false)}
        onView={(p) => {
          setListPanelOpen(false);
          handleView(p);
        }}
        onEdit={(p) => {
          setListPanelOpen(false);
          handleEdit(p);
        }}
        onDelete={handleDelete}
        onToggleActive={handleToggleActive}
      />

      {/* Fan read-only viewer — lightweight, always available. */}
      <PollViewModal
        visible={!!viewPoll}
        poll={viewPoll}
        teamId={teamId}
        onClose={() => setViewPoll(null)}
        onVoted={handleVoted}
      />

      {/* Admin panels — lazy-loaded, mounted only when needed. */}
      {canManage && (
        <Suspense fallback={null}>
          {(formMode || editingPoll) && (
            <PollForm
              visible={!!formMode}
              teamId={teamId}
              mode={formMode || 'create'}
              poll={editingPoll}
              onClose={() => {
                setFormMode(null);
                setEditingPoll(null);
              }}
              onSaved={handleSaved}
            />
          )}

          {resultsPoll && (
            <PollResultsModal
              visible={!!resultsPoll}
              poll={resultsPoll}
              onClose={() => setResultsPoll(null)}
            />
          )}
        </Suspense>
      )}

      <IrreversableModal
        visible={!!deleteTarget}
        heading={t('pollQuizProfile.deletePollHeading')}
        topText={deleteTarget ? `"${deleteTarget.question}"` : ''}
        text={t('pollQuizProfile.deletePollText')}
        confirmText={deleting ? t('pollQuizProfile.deleting') : t('pollQuizProfile.delete')}
        waiting={deleting}
        onConfirm={confirmDelete}
        closeModal={() => !deleting && setDeleteTarget(null)}
        confirmNoClose
      />
    </>
  );
};

export default PollsRow;
